import { browser } from "$app/env";
import { hashKey } from "@tanstack/svelte-query";
import type { QueryKey } from "@tanstack/svelte-query";
import { HELD, queryClient } from "./query.ts";
import { READ_BUDGET_MS } from "./read-budget.ts";
import { perReader } from "./reader.ts";

const fresh = perReader(() => ({ keys: new Set<string>(), painting: true }));

export const markFresh = (queryKey: QueryKey): void => {
  fresh().keys.add(hashKey(queryKey));
};

export const takeFresh = (queryKey: QueryKey): boolean => fresh().keys.delete(hashKey(queryKey));

export const heldWhileFresh = <Read extends { readonly queryKey: QueryKey }>(read: Read): Read =>
  fresh().keys.has(hashKey(read.queryKey)) ? { ...read, ...HELD } : read;

export const firstPaint = (): boolean => fresh().painting;

export const forgetFresh = (): void => {
  fresh().keys.clear();
  fresh().painting = false;
};

export const prefetched = <Read extends { readonly queryKey: QueryKey }>(
  read: Read,
): Promise<void> => queryClient().prefetchQuery(heldWhileFresh(read));

type Reads = readonly (Promise<unknown> | null)[];

const within = async (reads: Reads): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const budget = new Promise<void>((done) => {
    timer = setTimeout(done, READ_BUDGET_MS);
  });
  await Promise.race([Promise.allSettled(reads), budget]);
  clearTimeout(timer);
};

export const painted = async (reads: Reads): Promise<void> => {
  if (!browser) await Promise.allSettled(reads);
  else if (firstPaint()) await within(reads);
};

export const paintedWithin = async (reads: Reads): Promise<void> => {
  if (!browser || firstPaint()) await within(reads);
};
